import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { 
  UserGroupIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  BuildingOffice2Icon,
  AcademicCapIcon,
  ArrowLeftIcon
} from "@heroicons/react/24/outline";
import DashboardHeader from "../../../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../../components/utils/ResourceEmpty";
import RoleOnly, { useUserHasAccess } from "../../../../../routes/roleOnly";
import { ModuleFilter } from "../../../../../components/utils/ModuleFilter";
import { getRoles } from "../../../../../api/hse/competency";
import { fetchDepartments } from "../../../../../api/department";
import { toast } from "react-hot-toast";

/**
 * HSE/KS Competency Roles List - List all roles
 * ISO 9001:2015 Clause 7.2 - Competence
 */
export const HseKsCompetencyRolesList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filters, setFilters] = useState({
    query: "",
    departments: [],
  });
  
  const canManage = useUserHasAccess({ role: "hse_ks_competency_manage", action: "read" });

  useEffect(() => {
    fetchDepartmentsList();
    fetchRoles();
  }, [filters]);

  const fetchDepartmentsList = async () => {
    try {
      const response = await fetchDepartments({});
      // Ensure we always have an array
      let departmentsList = [];
      if (Array.isArray(response)) {
        departmentsList = response;
      } else if (response?.departments && Array.isArray(response.departments)) {
        departmentsList = response.departments;
      } else if (response?.data && Array.isArray(response.data)) {
        departmentsList = response.data;
      }
      setDepartments(departmentsList);
    } catch (err) {
      console.error("Error fetching departments:", err);
      setDepartments([]); // Set to empty array on error
    }
  };

  const fetchRoles = async () => {
    try {
      setLoading(true);
      setError(false);
      
      const apiFilters = {
        search: filters.query,
        department: filters.departments.length > 0 ? filters.departments[0] : undefined,
      };
      
      const response = await getRoles(apiFilters);
      setRoles(response.roles || []);
    } catch (err) {
      console.error("Error fetching roles:", err);
      setError(true);
      toast.error(t("Kunne ikke laste roller"));
    } finally {
      setLoading(false);
    }
  };

  const handleFiltersChange = (newFilters) => {
    setFilters({
      query: newFilters.query || "",
      departments: newFilters.departments || [],
    });
  };

  // Department options for ModuleFilter
  const departmentOptions = Array.isArray(departments) ? departments.map(dept => ({
    value: dept.id,
    label: dept.name,
  })) : [];

  // Custom dropdown filters
  const customDropdownFilters = [
    {
      key: "departments",
      label: t("Avdeling"),
      icon: BuildingOffice2Icon,
      options: departmentOptions.map(dept => ({
        value: dept.value,
        label: dept.label,
        class: "bg-blue-100 text-blue-800",
      })),
    },
  ];

  const headerActions = canManage ? [
    {
      label: t("Ny rolle"),
      icon: PlusIcon,
      path: "/dashboard/hse-ks/competency/roles/new",
      hidden: false,
      menu: false,
    },
  ] : [];

  if (loading) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={[t("Kunne ikke laste roller")]} />;
  }

  return (
    <DashboardHeader
      title={t("Roller")}
      subtitle={t("Administrer roller og kompetansekrav")}
      actions={headerActions}
      backButton={{
        label: t("Tilbake til kompetanse"),
        action: () => navigate("/dashboard/hse-ks/competency"),
        icon: ArrowLeftIcon,
      }}
    >
      <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
          {/* ModuleFilter */}
          <ModuleFilter
            onFiltersChange={handleFiltersChange}
            initialFilters={filters}
            searchPlaceholder={t("Søk i roller...")}
            customDropdownFilters={customDropdownFilters}
          />

          {/* Roles List */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t("Roller")} ({roles.length})
              </h3>
              
              {roles.length === 0 ? (
                <ResourceEmpty message={t("Ingen roller funnet")} />
              ) : (
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                          {t("Rolle")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Avdeling")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Påkrevde kompetanser")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Ansatte")}
                        </th>
                        <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">{t("Handlinger")}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {roles.map((role) => (
                        <tr key={role.id} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                            <div className="flex items-center">
                              <UserGroupIcon className="h-5 w-5 text-blue-600 mr-3" />
                              <div className="font-medium text-gray-900">{role.name}</div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {role.department_name || role.department ? (
                              <span className="flex items-center">
                                <BuildingOffice2Icon className="h-4 w-4 mr-1" />
                                {role.department_name || role.department}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <span className="flex items-center">
                              <AcademicCapIcon className="h-4 w-4 mr-1 text-blue-600" />
                              {role.required_competencies_count || 0}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {role.employees_count || 0}
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <div className="flex items-center justify-end space-x-2">
                              <Link
                                to={`/dashboard/hse-ks/competency/matrix/${role.id}`}
                                className="text-indigo-600 hover:text-indigo-900 inline-flex items-center"
                                title={t("Vis matrise")}
                              >
                                <EyeIcon className="h-4 w-4" />
                              </Link>
                              {canManage && (
                                <Link
                                  to={`/dashboard/hse-ks/competency/roles/${role.id}/edit`}
                                  className="text-gray-600 hover:text-gray-900 inline-flex items-center"
                                  title={t("Rediger")}
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </Link>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardHeader>
  );
};

